
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { companyActions } from '@nxsys/store/slices/company/reducer/companySlice';
import { GetActivityLogCompanyListParams } from '@nxsys/store/slices/company/types';
import { Icons } from '@nxsys/types/icons';
import { ActivityLog } from '@nxsys/types/models/activityLogs';
import { ICompany } from '@nxsys/types/models/company';
import { ColumnsType } from 'antd/lib/table';
import { debounce } from 'lodash';
import LogDetailsModal from '../../activityLogs/Modals/LogDetailsModal';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

type Props = {
  data?: ICompany;
};

const ActivityLogTab: React.FC<Props> = ({ data }) => {
  const { messages } = useTypedIntl();
  const dispatch = useDispatch();

  const { activityLogCompanyList } = useSelector(
    (state: AppState) => state.companyStore,
  );
  const { loading, list, pagination, queryParams } = activityLogCompanyList;

  const [selectedData, setSelectedData] = useState<ActivityLog | null>(null);
  const [openViewModal, setOpenViewModal] = useState(false);

  useEffect(() => {
    if (data?.id) {
      handleFetchData({
        page: 0,
        size: 20,
      });
    }
  }, [data?.id]);

  const handleFetchData = (params: GetActivityLogCompanyListParams) => {
    const newParams = { ...params, companyId: data?.id || '' };
    dispatch(companyActions.getActivityLogCompanyList(newParams));
  };

  const debouncedFetchData = useCallback(
    debounce((search: string) => {
      handleFetchData({
        ...queryParams,
        page: 0,
        size: 20,
        search: search,
      });
    }, 500),
    [queryParams],
  );

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const onChangeTable = (sorter?: { field: string; order: 'desc' | 'asc' }) => {
    if (sorter?.order) {
      handleFetchData({
        ...queryParams,
        sortField: sorter.field ?? '',
        sortType: sorter.order,
      });
    } else {
      handleFetchData({
        ...queryParams,
        sortField: undefined,
        sortType: undefined,
      });
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    debouncedFetchData(value);
  };

  const toggleViewModal = (record?: ActivityLog) => {
    setOpenViewModal(!openViewModal);
    setSelectedData(record || null);
  };

  const columns: ColumnsType<ActivityLog> = [
    {
      key: 'action',
      title: messages['common.action'],
      align: 'left',
      render: (record) => (
        <ul className="flex items-center justify-start gap-x-2">
          <li onClick={() => toggleViewModal(record)}>
            <Icons.IconEye />
          </li>
        </ul>
      ),
      width: 100,
    },
    {
      key: 'logDetails',
      title: messages['company.activityLog.logDetails'],
      dataIndex: 'logDetails',
      width: 640,
    },
    {
      key: 'updatedAt',
      title: messages['company.activityLog.lastUpdated'],
      dataIndex: 'updatedAt',
      width: 200,
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) =>
        value
          ? moment.utc(value).local().format('DD/MM/YYYY @ hh.mm a')
          : '_',
    },
    {
      key: 'updatedBy',
      title: messages['company.activityLog.updatedBy'],
      dataIndex: 'updatedBy',
      width: 200,
      render: (value) =>
        value
          ? `${value?.forename || ''} ${value?.surname || ''}`
          : messages['company.activityLog.superAdmin'],
    },
  ];

  return (
    <>
      <div className="p-[16px]">
        <div className="flex justify-between items-center mb-[16px]">
          <AppInputSearch onChange={handleSearchChange} />
        </div>
        <AppsContent>
          <AppTableContainer
            data={list ?? []}
            loading={loading}
            page={pagination.current}
            total={pagination.totalDocs}
            pageSize={pagination.pageSize}
            scroll={{ x: 1140, y: 600 }}
            titleEmpty={messages['common.noData']}
            columns={columns}
            onChangePagination={onChangePagination}
            onChangeTable={onChangeTable}
          />
        </AppsContent>
      </div>
      {selectedData && (
        <LogDetailsModal
          visible={openViewModal}
          data={selectedData as any}
          toggle={toggleViewModal}
          type="company"
        />
      )}
    </>
  );
};

export default ActivityLogTab;
